import { GamePack, GamePackIcon, GamePackPalette } from '@/API';
import { updateGamePack } from '@/client/gamePacks';
import { listOrganizations } from '@/client/organization';
import LoadingDots from '@/components/Animated/Icons/LoadingDots';
import { gamePackIconOptions, gamePackPaletteOptions, getGamePack } from '@/utils/gamePacks';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

interface CreateGamePackDialogProps {
  gamePackId?: string;

  isOpen: boolean;

  setIsOpen: (isOpen: boolean) => void;
  onSubmit?: () => void;
}

type SelectedOrganization = {
  id?: string;
  name?: string;
  selected: boolean;
};

type GamePackInputs = {
  name: string;
  description: string;
  category1: string;
  category2: string;
  category3: string;
  icon: GamePackIcon;
  palette: GamePackPalette;
  status: string;
  showInDiscover: boolean;
  isNew: boolean;
  orgAccessAll: string;
  organizations: SelectedOrganization[];
};

export const EditGamePackDialog = ({
  setIsOpen,
  isOpen,
  onSubmit,
  gamePackId
}: CreateGamePackDialogProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Keep previous state for potential UX improvements (e.g., comparison), currently unused
  // const [previousGamePack, setPreviousGamePack] = useState<GamePack | undefined>(undefined);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setFocus,
    watch
  } = useForm<GamePackInputs>({
    mode: 'onSubmit',
    defaultValues: {
      orgAccessAll: 'false',
      status: 'true',
      showInDiscover: false,
      isNew: true,
      category1: '',
      category2: '',
      category3: '',
      organizations: []
    }
  });
  const { fields, replace: replaceOrgs } = useFieldArray({
    control,
    name: 'organizations' // unique name for your Field Array,
  });
  const orgAccessAllValue = watch('orgAccessAll');
  const statusValue = watch('status');
  const watchedOrgs = (watch('organizations') as any[]) || [];
  const hasAtLeastOneSelected = watchedOrgs.some((o: any) => o?.selected === true);
  const isSaveDisabled =
    submitDisabled || (orgAccessAllValue === 'false' && !hasAtLeastOneSelected);

  useEffect(() => {
    const fetchAndSetupGamePack = async () => {
      if (!isOpen || !gamePackId) return;

      // setPreviousGamePack(undefined);
      reset();
      setIsLoading(true);

      try {
        const result = await getGamePack(gamePackId);
        const gamePack = result.data?.gamePackGet as GamePack;
        // setPreviousGamePack(gamePack);

        const selectedOrgIds = (gamePack?.organizations ?? [])
          .filter((item) => item !== undefined)
          .map((item) => item?.id)
          .filter(Boolean) as string[];

        const gamePackOrgAccessAll = gamePack?.organizations?.length === 0 ? 'true' : 'false';
        const gamePackStatus = (gamePack as any)?.status === 'ACTIVE' ? 'true' : 'false';

        setValue('name', gamePack?.name ?? '');
        setValue('description', gamePack?.description ?? '');
        setValue('category1', gamePack?.category1 ?? '');
        setValue('category2', gamePack?.category2 ?? '');
        setValue('category3', gamePack?.category3 ?? '');
        setValue('icon', gamePack?.icon ?? '');
        setValue('palette', gamePack?.palette ?? '');
        setValue('status', gamePackStatus);
        setValue('showInDiscover', gamePack?.showInDiscover ?? false);
        setValue('isNew', gamePack?.isNew ?? true);
        setValue('orgAccessAll', gamePackOrgAccessAll);
        setFocus('name');

        const allOrgsResult = await listOrganizations();
        const fetchedOrgs = (allOrgsResult ?? [])
          .filter((item) => item !== undefined && item !== null)
          .map((item) => ({
            id: (item as any).id as string,
            name: (item as any).name as string,
            selected: selectedOrgIds.includes(((item as any).id as string) ?? '')
          })) as SelectedOrganization[];

        replaceOrgs(fetchedOrgs);
      } catch (error) {
        console.error('Error fetching game pack:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetupGamePack();
  }, [isOpen, gamePackId, reset, setValue, setFocus, replaceOrgs]);

  const onEdit = async (data: GamePackInputs) => {
    if (!gamePackId) {
      throw new Error('Game pack id not provided');
    }

    setSubmitDisabled(true);
    try {
      const isOrgAccessAll = data.orgAccessAll === 'true';
      const selectedOrgs = isOrgAccessAll ? [] : data.organizations.filter((org) => org.selected);
      const organizationIds = selectedOrgs.map((org) => org.id).filter(Boolean) as string[];

      const sanitizeCategory = (value?: string) => {
        const trimmed = value?.trim() ?? '';
        return trimmed.slice(0, 12);
      };

      // Update the GamePack with organization associations
      await updateGamePack({
        id: gamePackId,
        name: data.name,
        description: data.description,
        icon: data.icon,
        palette: data.palette,
        status: data.status === 'true' ? 'ACTIVE' : 'DISABLED',
        showInDiscover: data.showInDiscover,
        isNew: data.isNew,
        organizationIds: organizationIds,
        category1: sanitizeCategory(data.category1),
        category2: sanitizeCategory(data.category2),
        category3: sanitizeCategory(data.category3)
      });

      setIsOpen(false);
      reset();
      if (onSubmit) {
        onSubmit();
      }
    } catch (err) {
      console.error({ err });
      throw new Error('Could not update game pack');
    } finally {
      setSubmitDisabled(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth={'md'} fullWidth={true}>
      <DialogTitle className="text-[24px] font-bold">Edit Game Pack</DialogTitle>
      <DialogContent>
        {isLoading && <LoadingDots />}
        <form onSubmit={handleSubmit(onEdit)}>
          {/* Title */}
          <DialogContentText className="text-black">Game Pack Title</DialogContentText>
          <TextField
            className="mb-[36px]"
            margin="dense"
            id="0"
            type="text"
            fullWidth
            autoFocus
            variant="outlined"
            placeholder="Give this Game Pack a unique name"
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', {
              required: 'Game pack name is required'
            })}
            disabled={isLoading}
          />
          {/* Description */}
          <DialogContentText className="text-black">Description</DialogContentText>
          <TextField
            className="mb-[36px]"
            margin="dense"
            id="0"
            type="text"
            fullWidth
            variant="outlined"
            error={!!errors.description}
            helperText={errors.description?.message ?? `${watch('description')?.length ?? 0}/100`}
            {...register('description', {
              required: 'Game pack description is required',
              maxLength: 100
            })}
            disabled={isLoading}
          />
          {/* Categories */}
          <DialogContentText className="text-black">Categories (optional)</DialogContentText>
          <div className="grid grid-cols-1 gap-12 md:grid-cols-3 mb-36">
            <TextField
              margin="dense"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Category 1"
              inputProps={{ maxLength: 12 }}
              error={!!errors.category1}
              helperText={errors.category1?.message ?? `${watch('category1')?.length ?? 0}/12`}
              {...register('category1', {
                maxLength: { value: 12, message: '12 characters max' }
              })}
              disabled={isLoading}
            />
            <TextField
              margin="dense"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Category 2"
              inputProps={{ maxLength: 12 }}
              error={!!errors.category2}
              helperText={errors.category2?.message ?? `${watch('category2')?.length ?? 0}/12`}
              {...register('category2', {
                maxLength: { value: 12, message: '12 characters max' }
              })}
              disabled={isLoading}
            />
            <TextField
              margin="dense"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Category 3"
              inputProps={{ maxLength: 12 }}
              error={!!errors.category3}
              helperText={errors.category3?.message ?? `${watch('category3')?.length ?? 0}/12`}
              {...register('category3', {
                maxLength: { value: 12, message: '12 characters max' }
              })}
              disabled={isLoading}
            />
          </div>
          <DialogContentText className="text-black">Icon</DialogContentText>
          <Select
            fullWidth
            id="icon-select"
            margin="dense"
            {...register('icon')}
            className="mb-[36px]"
            variant="outlined"
            value={watch('icon') || ''}
          >
            {gamePackIconOptions.map((iconOption) => (
              <MenuItem value={iconOption.value} key={iconOption.value}>
                {iconOption.label}
              </MenuItem>
            ))}
          </Select>
          <DialogContentText className="text-black">Palette</DialogContentText>
          <Select
            fullWidth
            id="palette-select"
            margin="dense"
            {...register('palette')}
            className="mb-[36px]"
            variant="outlined"
            value={watch('palette') || ''}
          >
            {gamePackPaletteOptions.map((paletteOption) => (
              <MenuItem value={paletteOption.value} key={paletteOption.value}>
                {paletteOption.label}
              </MenuItem>
            ))}
          </Select>
          <div>
            {/* Status */}
            <FormControl className="mb-32 mr-64">
              <FormLabel id="status-radio-buttons-group-label" className="text-black">
                Game Pack Status
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="status-radio-buttons-group-label"
                value={statusValue}
              >
                <FormControlLabel
                  {...register('status')}
                  value={'true'}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Active"
                  disabled={isLoading}
                />
                <FormControlLabel
                  {...register('status')}
                  value={'false'}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Disabled"
                  disabled={isLoading}
                />
              </RadioGroup>
            </FormControl>
            {/* Homepage Visibility */}
            <FormControl className="mb-32 mr-64">
              <FormLabel className="text-black">Homepage Visibility</FormLabel>
              <Controller
                name="showInDiscover"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        onChange={(_event, checked) => field.onChange(checked)}
                        disabled={isLoading}
                        sx={{
                          '&.Mui-checked': { color: '#F58C29' },
                          '& .MuiSvgIcon-root': {
                            fontSize: 32
                          }
                        }}
                      />
                    }
                    label="Show in the Discover section"
                  />
                )}
              />
            </FormControl>
            {/* New Gamepack */}
            <FormControl className="mb-32 mr-64">
              <FormLabel className="text-black">New Game Pack</FormLabel>
              <Controller
                name="isNew"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        onChange={(_event, checked) => field.onChange(checked)}
                        disabled={isLoading}
                        sx={{
                          '&.Mui-checked': { color: '#F58C29' },
                          '& .MuiSvgIcon-root': {
                            fontSize: 32
                          }
                        }}
                      />
                    }
                    label="Is New"
                  />
                )}
              />
            </FormControl>
          </div>
          <div>
            {/* Org Access */}
            <FormControl className="mb-10">
              <FormLabel id="demo-row-radio-buttons-group-label" className="text-black">
                Organization Access
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                value={orgAccessAllValue}
              >
                <FormControlLabel
                  {...register('orgAccessAll')}
                  value={'true'}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="All"
                  disabled={isLoading}
                />
                <FormControlLabel
                  {...register('orgAccessAll')}
                  value={'false'}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Selected Organizations Only"
                  disabled={isLoading}
                />
              </RadioGroup>
            </FormControl>
            {orgAccessAllValue === 'false' ? (
              <>
                <Stack>
                  {fields.map((field, index) => {
                    return (
                      <FormControlLabel
                        key={`org-checkbox-${field.id}`}
                        control={
                          <Checkbox
                            defaultChecked={field.selected}
                            {...register(`organizations.${index}.selected`)}
                          />
                        }
                        label={field.name}
                      />
                    );
                  })}
                </Stack>
              </>
            ) : (
              <></>
            )}
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <button
          className="btn btn-neutral secondary mr-[5px]"
          onClick={() => {
            setIsOpen(false);
          }}
        >
          Cancel
        </button>
        <button
          disabled={isSaveDisabled}
          className="btn btn-primary"
          onClick={handleSubmit(onEdit)}
        >
          Save
        </button>
      </DialogActions>
    </Dialog>
  );
};
